import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiCheck,
  FiChevronRight,
  FiCompass,
  FiExternalLink,
  FiStar,
  FiZap,
} from 'react-icons/fi';
import { getApiUrl } from '../utils/api';

type Intent = { value: string; label: string; description?: string | null };
type Complexity = 'beginner' | 'intermediate' | 'advanced';

interface Candidate {
  kind: 'template' | 'insight';
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  score: number;
  score_breakdown: Record<string, number>;
  template_slug: string | null;
  paper_url: string | null;
  paper_source: string | null;
  complexity: Complexity | null;
  gpu_required: boolean | null;
  corroborated: boolean | null;
  relevance: 'high' | 'medium' | 'low' | 'none' | null;
  is_new_this_week: boolean | null;
}

interface MatchResponse {
  candidates: Candidate[];
  intents_expanded: string[];
}

type Step = 'intent' | 'constraints' | 'results';

export default function StrategyComposerPage() {
  const [step, setStep] = useState<Step>('intent');

  const [intents, setIntents] = useState<Intent[]>([]);
  const [selectedIntents, setSelectedIntents] = useState<Set<string>>(new Set());

  const [complexity, setComplexity] = useState<Complexity | 'any'>('any');
  const [gpuMode, setGpuMode] = useState<'any' | 'gpu_ok' | 'cpu_only'>('any');
  const [assetClass, setAssetClass] = useState<string>('any');
  const [goalText, setGoalText] = useState<string>('');

  const [loadingIntents, setLoadingIntents] = useState(true);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [intentsExpanded, setIntentsExpanded] = useState<string[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${getApiUrl()}/api/v1/strategy-composer/intents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`intents ${resp.status}`);
        setIntents(await resp.json());
      } catch (e) {
        console.error(e);
        setError('Could not load intent options.');
      } finally {
        setLoadingIntents(false);
      }
    })();
  }, [navigate]);

  const toggleIntent = (value: string) => {
    const next = new Set(selectedIntents);
    if (next.has(value)) next.delete(value); else next.add(value);
    setSelectedIntents(next);
  };

  const runMatch = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setLoadingMatch(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        intents: Array.from(selectedIntents),
        limit: 12,
      };
      if (complexity !== 'any') body.complexity = complexity;
      if (gpuMode === 'gpu_ok') body.gpu_ok = true;
      if (gpuMode === 'cpu_only') body.gpu_ok = false;
      if (assetClass !== 'any') body.asset_class = assetClass;
      if (goalText.trim()) body.goal_text = goalText.trim();

      const resp = await fetch(`${getApiUrl()}/api/v1/strategy-composer/match`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`match ${resp.status}: ${text.slice(0, 200)}`);
      }
      const data = (await resp.json()) as MatchResponse;
      setCandidates(data.candidates);
      setIntentsExpanded(data.intents_expanded);
      setStep('results');
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Match failed.');
    } finally {
      setLoadingMatch(false);
    }
  }, [navigate, selectedIntents, complexity, gpuMode, assetClass, goalText]);

  const pickCandidate = (c: Candidate) => {
    if (c.kind === 'template' && c.template_slug) {
      sessionStorage.setItem(
        'strategy_library_prefilter',
        JSON.stringify({ from: 'composer', slug: c.template_slug }),
      );
      navigate('/dashboard/strategy-library');
      return;
    }
    if (c.kind === 'insight' && c.paper_url) {
      window.open(c.paper_url, '_blank', 'noopener');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <header className="mb-8">
          <div className="flex items-center gap-3 text-neutral-400">
            <FiCompass className="h-5 w-5" />
            <span className="text-sm uppercase tracking-wide">Strategy Composer</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold">What are you trying to build?</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400">
            Pick your intent, add a few constraints, and get ranked starting points pulled from the
            Strategy Template Library and the Research Feed. Deterministic matching — no LLM
            guessing, no black-box ranking.
          </p>
        </header>

        <Stepper current={step} />

        {error && (
          <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {step === 'intent' && (
          <IntentStep
            intents={intents}
            loading={loadingIntents}
            selected={selectedIntents}
            onToggle={toggleIntent}
            onNext={() => setStep('constraints')}
          />
        )}

        {step === 'constraints' && (
          <ConstraintsStep
            complexity={complexity}
            setComplexity={setComplexity}
            gpuMode={gpuMode}
            setGpuMode={setGpuMode}
            assetClass={assetClass}
            setAssetClass={setAssetClass}
            goalText={goalText}
            setGoalText={setGoalText}
            loading={loadingMatch}
            onBack={() => setStep('intent')}
            onRun={runMatch}
          />
        )}

        {step === 'results' && (
          <ResultsStep
            candidates={candidates}
            intentsExpanded={intentsExpanded}
            onBack={() => setStep('constraints')}
            onPick={pickCandidate}
          />
        )}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const items: { key: Step; label: string }[] = [
    { key: 'intent', label: '1. Intent' },
    { key: 'constraints', label: '2. Constraints' },
    { key: 'results', label: '3. Starting points' },
  ];
  return (
    <nav className="mb-6 flex items-center gap-2 text-xs">
      {items.map((it, i) => (
        <div key={it.key} className="flex items-center gap-2">
          <span
            className={`rounded-md border px-3 py-1 ${
              current === it.key
                ? 'border-white bg-white text-black font-medium'
                : 'border-neutral-800 bg-neutral-950 text-neutral-500'
            }`}
          >
            {it.label}
          </span>
          {i < items.length - 1 && <FiChevronRight className="h-3.5 w-3.5 text-neutral-600" />}
        </div>
      ))}
    </nav>
  );
}

function IntentStep({
  intents, loading, selected, onToggle, onNext,
}: {
  intents: Intent[];
  loading: boolean;
  selected: Set<string>;
  onToggle: (v: string) => void;
  onNext: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white" />
      </div>
    );
  }
  return (
    <div>
      <p className="mb-4 text-sm text-neutral-400">
        Pick one or more. Tags combine (union) when matching — more chips = broader results.
      </p>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {intents.map((it) => {
          const on = selected.has(it.value);
          return (
            <button
              key={it.value}
              type="button"
              onClick={() => onToggle(it.value)}
              className={`flex flex-col rounded-lg border p-3 text-left transition ${
                on
                  ? 'border-white bg-neutral-900'
                  : 'border-neutral-800 bg-neutral-950 hover:border-neutral-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">{it.label}</span>
                {on && <FiCheck className="h-4 w-4 text-white" />}
              </div>
              {it.description && (
                <span className="mt-1 text-xs text-neutral-500">{it.description}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onNext}
          disabled={selected.size === 0}
          className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next: constraints <FiArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ConstraintsStep({
  complexity, setComplexity,
  gpuMode, setGpuMode,
  assetClass, setAssetClass,
  goalText, setGoalText,
  loading, onBack, onRun,
}: {
  complexity: Complexity | 'any';
  setComplexity: (c: Complexity | 'any') => void;
  gpuMode: 'any' | 'gpu_ok' | 'cpu_only';
  setGpuMode: (m: 'any' | 'gpu_ok' | 'cpu_only') => void;
  assetClass: string;
  setAssetClass: (a: string) => void;
  goalText: string;
  setGoalText: (t: string) => void;
  loading: boolean;
  onBack: () => void;
  onRun: () => void;
}) {
  return (
    <div className="rounded-lg border border-neutral-900 bg-neutral-950 p-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label className="flex flex-col text-xs text-neutral-500">
          <span className="mb-1 uppercase tracking-wide">Complexity</span>
          <select
            value={complexity}
            onChange={(e) => setComplexity(e.target.value as Complexity | 'any')}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm text-white focus:border-neutral-600 focus:outline-none"
          >
            <option value="any">Any</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-neutral-500">
          <span className="mb-1 uppercase tracking-wide">Compute</span>
          <select
            value={gpuMode}
            onChange={(e) => setGpuMode(e.target.value as typeof gpuMode)}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm text-white focus:border-neutral-600 focus:outline-none"
          >
            <option value="any">Any</option>
            <option value="gpu_ok">GPU OK</option>
            <option value="cpu_only">CPU only</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-neutral-500">
          <span className="mb-1 uppercase tracking-wide">Asset class</span>
          <select
            value={assetClass}
            onChange={(e) => setAssetClass(e.target.value)}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm text-white focus:border-neutral-600 focus:outline-none"
          >
            <option value="any">Any</option>
            <option value="crypto">Crypto</option>
            <option value="equity">Equity</option>
            <option value="options">Options</option>
            <option value="fx">FX</option>
            <option value="fixed_income">Fixed Income</option>
          </select>
        </label>
      </div>

      <label className="mt-4 block text-xs text-neutral-500">
        <span className="mb-1 uppercase tracking-wide">Goal (optional free text)</span>
        <textarea
          value={goalText}
          onChange={(e) => setGoalText(e.target.value)}
          rows={3}
          placeholder="e.g. 'test a momentum strategy on BTC perps with a 1h bar, gated by a volatility regime filter'"
          className="w-full resize-none rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none"
        />
      </label>

      <div className="mt-5 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-md border border-neutral-800 px-3 py-1.5 text-xs text-neutral-400 transition hover:border-neutral-600 hover:text-neutral-200"
        >
          <FiArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button"
          onClick={onRun}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Matching...' : (<>Find starting points <FiArrowRight className="h-4 w-4" /></>)}
        </button>
      </div>
    </div>
  );
}

function ResultsStep({
  candidates, intentsExpanded, onBack, onPick,
}: {
  candidates: Candidate[];
  intentsExpanded: string[];
  onBack: () => void;
  onPick: (c: Candidate) => void;
}) {
  const hasTemplates = useMemo(() => candidates.some((c) => c.kind === 'template'), [candidates]);
  const hasInsights = useMemo(() => candidates.some((c) => c.kind === 'insight'), [candidates]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
        <span>Matched tags:</span>
        {intentsExpanded.map((t) => (
          <span key={t} className="rounded-sm bg-neutral-800/70 px-1.5 py-0.5 text-neutral-300">
            #{t}
          </span>
        ))}
      </div>

      {candidates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-800 bg-neutral-950 py-16 text-center">
          <FiCompass className="mx-auto h-8 w-8 text-neutral-600" />
          <h2 className="mt-4 text-lg font-medium text-neutral-200">Nothing matched yet</h2>
          <p className="mt-2 text-sm text-neutral-500">
            Loosen a constraint or add another intent chip. The Research Feed fills in fresh
            papers every week — revisit after the next digest drops.
          </p>
        </div>
      ) : (
        <>
          {hasTemplates && (
            <>
              <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-neutral-400">
                Ready to clone
              </h2>
              <div className="mb-6 flex flex-col gap-3">
                {candidates.filter((c) => c.kind === 'template').map((c) => (
                  <CandidateCard key={c.id} candidate={c} onPick={onPick} />
                ))}
              </div>
            </>
          )}
          {hasInsights && (
            <>
              <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-neutral-400">
                Paper-backed leads (build from scratch)
              </h2>
              <div className="flex flex-col gap-3">
                {candidates.filter((c) => c.kind === 'insight').map((c) => (
                  <CandidateCard key={c.id} candidate={c} onPick={onPick} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="mt-6 flex justify-start">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-md border border-neutral-800 px-3 py-1.5 text-xs text-neutral-400 transition hover:border-neutral-600 hover:text-neutral-200"
        >
          <FiArrowLeft className="h-3.5 w-3.5" /> Adjust constraints
        </button>
      </div>
    </div>
  );
}

function CandidateCard({ candidate: c, onPick }: { candidate: Candidate; onPick: (c: Candidate) => void }) {
  const isTemplate = c.kind === 'template';
  return (
    <article className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
            <span className="rounded border border-neutral-700 bg-neutral-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
              {isTemplate ? 'Template' : 'Paper'}
            </span>
            {isTemplate && c.complexity && (
              <span className="text-neutral-500">{c.complexity}</span>
            )}
            {isTemplate && c.gpu_required && (
              <span className="inline-flex items-center gap-1 text-purple-400">
                <FiZap className="h-3 w-3" /> GPU
              </span>
            )}
            {isTemplate && c.is_new_this_week && (
              <span className="text-emerald-400">New</span>
            )}
            {!isTemplate && c.relevance && (
              <span className="uppercase tracking-wide text-amber-300">{c.relevance}</span>
            )}
            {!isTemplate && c.corroborated && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <FiStar className="h-3 w-3" /> corroborated
              </span>
            )}
            <span className="ml-auto font-mono text-neutral-500">score {c.score}</span>
          </div>
          <h3 className="truncate text-base font-semibold text-white">{c.name}</h3>
          {c.description && (
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-400 line-clamp-3">
              {c.description}
            </p>
          )}
          {c.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {c.tags.slice(0, 6).map((t) => (
                <span key={t} className="rounded-sm bg-neutral-800/60 px-1.5 py-0.5 text-[10px] text-neutral-400">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onPick(c)}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
            isTemplate
              ? 'bg-white text-black hover:bg-neutral-200'
              : 'border border-neutral-700 text-neutral-200 hover:border-neutral-500'
          }`}
        >
          {isTemplate ? (
            <>
              <FiBookOpen className="h-3.5 w-3.5" /> Open in Library
            </>
          ) : (
            <>
              <FiExternalLink className="h-3.5 w-3.5" /> View paper
            </>
          )}
        </button>
      </div>
    </article>
  );
}
